import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { doc, setDoc } from "firebase/firestore";
import { getContactsByIds } from "../../utils/firebasefunctions";
import { firestored } from "../../firebase/firebaseConfig";
import { normalizeContact } from "../../utils/contactNormalize";
import { addContact, updateContact } from "../../utils/contactsApi";
import ContactEditDrawer from "../new-contact/ContactEditDrawer";
import AddClientPicker from "../new-contact/AddClientPicker";
import { useStateValue } from "../../context/StateProvider";
import { useActionDialog } from "../ActionDialog";

function ClientInform({ deal, onCustomersChange }) {
  const [{ user }] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const [customerdata, setcustomerdata] = useState([]);
  const [editing, setEditing] = useState(null); // existing contact (edit mode)
  const [creating, setCreating] = useState(false); // create-new mode flag
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (deal?.customers?.length > 0) {
      getcontacts();
    } else {
      setcustomerdata([]);
    }
  }, [deal?.customers]);

  const getcontacts = async () => {
    const contacts = await getContactsByIds(deal.customers);
    setcustomerdata(contacts);
  };

  const persistCustomers = async (nextCustomers) => {
    const ref = doc(firestored, "deals", String(deal.id || deal.createdat));
    await setDoc(ref, { customers: nextCustomers }, { merge: true });
    onCustomersChange?.(nextCustomers);
  };

  const addContactToDeal = async (contactId) => {
    const current = deal?.customers || [];
    if (current.includes(contactId)) {
      toast.info("That client is already on this deal");
      return;
    }
    try {
      const next = [...current, contactId];
      await persistCustomers(next);
      toast.success("Client added to deal");
    } catch (err) {
      console.error("Failed to add client:", err);
      toast.error("Failed to add client");
    }
  };

  const removeContactFromDeal = async (contactId) => {
    const ok = await confirm({
      title: "Remove client",
      body: "Remove this client from the deal?",
      note: "The contact record itself is kept.",
      tone: "danger",
      confirmLabel: "Remove",
    });
    if (!ok) return;
    try {
      const next = (deal?.customers || []).filter(
        (id) => String(id) !== String(contactId),
      );
      await persistCustomers(next);
      toast.success("Client removed from deal");
    } catch (err) {
      console.error("Failed to remove client:", err);
      toast.error("Failed to remove client");
    }
  };

  // === edit existing ===
  const openEdit = (rawContact) => {
    setEditing(normalizeContact(rawContact));
    setCreating(false);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
    setCreating(false);
  };

  const handleSaveEdit = async (form) => {
    setSaving(true);
    try {
      const result = await updateContact({ user, contact: editing, form });
      setcustomerdata((prev) =>
        prev.map((c) => (c.id === editing.id ? { ...c, ...result } : c)),
      );
      toast.success("Client updated");
      setEditing(null);
    } catch (err) {
      console.error("Client save failed:", err);
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // === create new (via the drawer) and attach to deal ===
  const handleCreateNew = async (form) => {
    setSaving(true);
    try {
      if (!user?.gmailtoken?.access_token) {
        toast.error("Connect Gmail in Settings before adding a new contact");
        return;
      }
      const created = await addContact({ user, contact: form });
      await addContactToDeal(created.firebaseId);
      setCreating(false);
    } catch (err) {
      console.error("Create contact failed:", err);
      toast.error(err.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <div className="border-b border-black/10 pb-3 mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Client Information</h3>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 border border-slate-300 hover:border-slate-500 rounded-md px-2.5 py-1.5 transition"
        >
          <Plus size={13} /> Add Client
        </button>
      </div>

      {customerdata.length === 0 && (
        <div className="text-sm text-slate-500 py-2">
          No clients on this deal yet. Use "Add Client" above to attach one.
        </div>
      )}

      {customerdata.map((c, i) => (
        <div
          key={c.id || i}
          className="mb-4 p-4 rounded-lg bg-gray-50 relative group"
        >
          <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
            <button
              type="button"
              onClick={() => openEdit(c)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:border-slate-400 hover:text-slate-900 rounded-md px-2 py-1 shadow-sm"
              aria-label="Edit client"
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              type="button"
              onClick={() => removeContactFromDeal(c.id)}
              className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-white border border-red-200 hover:border-red-400 hover:bg-red-50 rounded-md px-2 py-1 shadow-sm"
              aria-label="Remove client"
            >
              <Trash2 size={12} />
            </button>
          </div>
          <p>
            <span className="font-semibold">Name:</span> {c.name}
          </p>
          <p>
            <span className="font-semibold">Email:</span> {c.email}
          </p>
          <p>
            <span className="font-semibold">Phone:</span> {c.phone}
          </p>
        </div>
      ))}

      {deal?.customers?.some((c) => c?.clientName) && (
        <>
          <h3 className="text-lg font-semibold">Old Client Information</h3>
          {deal?.customers?.map(
            (c, i) =>
              c.clientName && (
                <div
                  key={`old-${i}`}
                  className="mb-4 p-4 rounded-lg bg-gray-50"
                >
                  <p>
                    <span className="font-semibold">Name:</span> {c.name}
                  </p>
                  <p>
                    <span className="font-semibold">Email:</span> {c.email}
                  </p>
                  <p>
                    <span className="font-semibold">Phone:</span> {c.phone}
                  </p>
                </div>
              ),
          )}
        </>
      )}

      {pickerOpen && (
        <AddClientPicker
          existingCustomerIds={deal?.customers || []}
          userId={user?.id}
          onClose={() => setPickerOpen(false)}
          onPick={async (contactId) => {
            setPickerOpen(false);
            await addContactToDeal(contactId);
          }}
          onCreateNew={() => {
            setPickerOpen(false);
            setCreating(true);
          }}
        />
      )}

      {editing && (
        <ContactEditDrawer
          initial={editing}
          onClose={closeEdit}
          onSubmit={handleSaveEdit}
          saving={saving}
        />
      )}

      {creating && (
        <ContactEditDrawer
          initial={null}
          onClose={closeEdit}
          onSubmit={handleCreateNew}
          saving={saving}
        />
      )}
      {dialog}
    </div>
  );
}

export default ClientInform;
